import React, { useEffect, useState, useRef, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { Navigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import { 
  Plus, Edit, Trash2, Save, X, Eye, EyeOff, 
  FileText, Settings, Users, LayoutDashboard,
  ChevronDown, ChevronUp, GripVertical, Image, Upload,
  User, Calendar, ShoppingBag, Layers
} from 'lucide-react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import StoreManagement from '../components/StoreManagement';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconOptions = ['Bot', 'Plane', 'Factory', 'Award', 'Trophy', 'Glasses', 'Box', 'Building2', 'GraduationCap'];
const galleryCategories = ['Robotics', 'Drones', 'Labs', 'Training', 'Events', 'RPTO'];

// Quill Editor Configuration
const quillModules = {
  toolbar: [
    [{ 'header': [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ 'list': 'ordered'}, { 'list': 'bullet' }],
    [{ 'indent': '-1'}, { 'indent': '+1' }],
    ['blockquote', 'code-block'],
    ['link', 'image'],
    [{ 'color': [] }, { 'background': [] }],
    [{ 'align': [] }],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline', 'strike',
  'list', 'bullet', 'indent', 'blockquote', 'code-block',
  'link', 'image', 'color', 'background', 'align'
];

const Admin = () => {
  const { user, token, loading } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [uploading, setUploading] = useState(false);
  const blogImageRef = useRef(null);
  const galleryImageRef = useRef(null);
  const slideImageRef = useRef(null);
  
  // Data states
  const [hero, setHero] = useState(null);
  const [heroSlides, setHeroSlides] = useState([]);
  const [about, setAbout] = useState(null);
  const [services, setServices] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [galleryItems, setGalleryItems] = useState([]);
  
  // UI states
  const [editingService, setEditingService] = useState(null);
  const [editingBlog, setEditingBlog] = useState(null);
  const [editingGallery, setEditingGallery] = useState(null);
  const [editingSlide, setEditingSlide] = useState(null);
  const [showServiceForm, setShowServiceForm] = useState(false);
  const [showBlogForm, setShowBlogForm] = useState(false);
  const [showGalleryForm, setShowGalleryForm] = useState(false);
  const [showSlideForm, setShowSlideForm] = useState(false);
  
  // Form states
  const [serviceForm, setServiceForm] = useState({
    title: '',
    description: '',
    features: [''],
    icon: 'Bot',
    is_active: true,
    order: 0
  });
  
  const [blogForm, setBlogForm] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    author: 'UAV Geo Solutions',
    image_url: '',
    tags: [''],
    is_published: false
  });

  const [galleryForm, setGalleryForm] = useState({
    title: '',
    category: 'Labs',
    image_url: '',
    description: '',
    order: 0
  });

  const [slideForm, setSlideForm] = useState({
    heading: '',
    subheading: '',
    category: 'Featured',
    image_url: '',
    cta_primary_text: 'Explore Services',
    cta_primary_link: '/services',
    cta_secondary_text: 'Get Proposal',
    cta_secondary_link: '/contact',
    display_order: 0,
    is_active: true
  });

  useEffect(() => {
    if (user?.role === 'admin') {
      fetchAllData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const fetchAllData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [heroRes, heroSlidesRes, aboutRes, servicesRes, contactsRes, blogRes, galleryRes] = await Promise.all([
        axios.get(`${API_URL}/hero`, { headers }),
        axios.get(`${API_URL}/hero-slides`, { headers }),
        axios.get(`${API_URL}/about`, { headers }),
        axios.get(`${API_URL}/services`, { headers }),
        axios.get(`${API_URL}/contact`, { headers }),
        axios.get(`${API_URL}/blog`, { headers }),
        axios.get(`${API_URL}/gallery`, { headers }),
      ]);
      setHero(heroRes.data);
      setHeroSlides(heroSlidesRes.data);
      setAbout(aboutRes.data);
      setServices(servicesRes.data);
      setContacts(contactsRes.data);
      setBlogPosts(blogRes.data);
      setGalleryItems(galleryRes.data);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      toast.error('Failed to fetch data');
    }
  };

  // Image upload handler
  const handleImageUpload = async (file, targetForm) => {
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
      });
      
      const imageUrl = response.data.url;
      
      if (targetForm === 'blog') {
        setBlogForm(prev => ({ ...prev, image_url: imageUrl }));
      } else if (targetForm === 'gallery') {
        setGalleryForm(prev => ({ ...prev, image_url: imageUrl }));
      } else if (targetForm === 'slide') {
        setSlideForm(prev => ({ ...prev, image_url: imageUrl }));
      }
      
      toast.success('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  // Hero handlers
  const handleHeroUpdate = async () => {
    try {
      await axios.put(`${API_URL}/hero`, hero, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Hero section updated!');
    } catch (error) {
      toast.error('Failed to update hero section');
    }
  };

  // About handlers
  const handleAboutUpdate = async () => {
    try {
      await axios.put(`${API_URL}/about`, about, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('About section updated!');
    } catch (error) {
      toast.error('Failed to update about section');
    }
  };

  // Service handlers
  const handleServiceSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...serviceForm,
        features: serviceForm.features.filter(f => f.trim() !== ''),
        service_type: serviceForm.title.toLowerCase().replace(/\s+/g, '_')
      };
      
      if (editingService) {
        await axios.put(`${API_URL}/services/${editingService.id}`, payload, { headers });
        toast.success('Service updated!');
      } else {
        await axios.post(`${API_URL}/services`, payload, { headers });
        toast.success('Service created!');
      }
      
      resetServiceForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save service');
    }
  };

  const handleEditService = (service) => {
    setServiceForm({
      title: service.title,
      description: service.description,
      features: service.features.length > 0 ? service.features : [''],
      icon: service.icon,
      is_active: service.is_active,
      order: service.order
    });
    setEditingService(service);
    setShowServiceForm(true);
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) return;
    try {
      await axios.delete(`${API_URL}/services/${serviceId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Service deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete service');
    }
  };

  const handleToggleServiceActive = async (service) => {
    try {
      await axios.put(`${API_URL}/services/${service.id}`, 
        { ...service, is_active: !service.is_active },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Service ${service.is_active ? 'deactivated' : 'activated'}!`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update service');
    }
  };

  const resetServiceForm = () => {
    setServiceForm({
      title: '',
      description: '',
      features: [''],
      icon: 'Bot',
      is_active: true,
      order: services.length
    });
    setEditingService(null);
    setShowServiceForm(false);
  };

  // Blog handlers
  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const payload = {
        ...blogForm,
        tags: blogForm.tags.filter(t => t.trim() !== ''),
        slug: blogForm.slug || blogForm.title.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')
      };
      
      if (editingBlog) {
        await axios.put(`${API_URL}/blog/${editingBlog.id}`, payload, { headers });
        toast.success('Blog post updated!');
      } else {
        await axios.post(`${API_URL}/blog`, payload, { headers });
        toast.success('Blog post created!');
      }
      
      resetBlogForm();
      fetchAllData();
    } catch (error) {
      toast.error('Failed to save blog post');
    }
  };

  const handleEditBlog = (post) => {
    setBlogForm({
      title: post.title,
      slug: post.slug,
      excerpt: post.excerpt,
      content: post.content,
      author: post.author,
      image_url: post.image_url || '',
      tags: post.tags.length > 0 ? post.tags : [''],
      is_published: post.is_published
    });
    setEditingBlog(post);
    setShowBlogForm(true);
  };

  const handleDeleteBlog = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this blog post?')) return;
    try {
      await axios.delete(`${API_URL}/blog/${postId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Blog post deleted!');
      fetchAllData();
    } catch (error) {
      toast.error('Failed to delete blog post');
    }
  };

  const handleToggleBlogPublish = async (post) => {
    try {
      await axios.put(`${API_URL}/blog/${post.id}`, 
        { ...post, is_published: !post.is_published },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Blog post ${post.is_published ? 'unpublished' : 'published'}!`);
      fetchAllData();
    } catch (error) {
      toast.error('Failed to update blog post');
    }
  };

  const resetBlogForm = () => {
    setBlogForm({
      title: '',
      slug: '',
      excerpt: '',
      content: '',
      author: 'UAV Geo Solutions',
      image_url: '',
      tags: [''],
      is_published: false
    });
    setEditingBlog(null);
    setShowBlogForm(false);
  };

  // Feature/Tag array handlers
  const addFeature = () => setServiceForm(prev => ({ ...prev, features: [...prev.features, ''] }));
  const removeFeature = (index) => setServiceForm(prev => ({ 
    ...prev, 
    features: prev.features.filter((_, i) => i !== index) 
  }));
  const updateFeature = (index, value) => setServiceForm(prev => ({
    ...prev,
    features: prev.features.map((f, i) => i === index ? value : f)
  }));

  const addTag = () => setBlogForm(prev => ({ ...prev, tags: [...prev.tags, ''] }));
  const removeTag = (index) => setBlogForm(prev => ({ 
    ...prev, 
    tags: prev.tags.filter((_, i) => i !== index) 
  }));
  const updateTag = (index, value) => setBlogForm(prev => ({
    ...prev,
    tags: prev.tags.map((t, i) => i === index ? value : t)
  }));

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  if (!user || user.role !== 'admin') {
    return <Navigate to="/login" replace />;
  }

  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { id: 'store', label: 'Store', icon: ShoppingBag },
    { id: 'hero', label: 'Hero Slides', icon: Layers },
    { id: 'about', label: 'About', icon: FileText },
    { id: 'services', label: 'Services', icon: Settings },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'gallery', label: 'Gallery', icon: Image },
    { id: 'contacts', label: 'Contacts', icon: Users },
  ];

  return (
    <div className="min-h-screen pt-28 pb-24 bg-gradient-to-br from-[#0a0e27] via-[#0f1535] to-[#0a0e27]">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h1 className="text-4xl md:text-5xl font-heading font-bold uppercase tracking-tighter mb-8 text-glow">
            Admin Dashboard
          </h1>

          {/* Tabs */}
          <div className="flex flex-wrap gap-2 mb-8">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  data-testid={`admin-tab-${tab.id}`}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold tracking-wider transition-all ${
                    activeTab === tab.id 
                      ? 'bg-primary text-black' 
                      : 'bg-white/5 text-gray-400 hover:bg-white/10 hover:text-primary'
                  }`}
                >
                  <Icon size={16} />
                  {tab.label}
                </button>
              );
            })}
          </div>

          {/* Dashboard Tab */}
          {activeTab === 'dashboard' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6">
              <div 
                onClick={() => setActiveTab('services')}
                className="glass rounded-2xl p-6 cursor-pointer hover:bg-white/10 transition-all group"
                data-testid="dashboard-services-card"
              >
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2 group-hover:text-primary transition-colors">Total Services</h3>
                <p className="text-4xl font-bold text-primary">{services.length}</p>
                <p className="text-sm text-gray-400 mt-1">{services.filter(s => s.is_active).length} active</p>
                <p className="text-xs text-primary mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to manage →</p>
              </div>
              <div 
                onClick={() => setActiveTab('blog')}
                className="glass rounded-2xl p-6 cursor-pointer hover:bg-white/10 transition-all group"
                data-testid="dashboard-blog-card"
              >
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2 group-hover:text-primary transition-colors">Blog Posts</h3>
                <p className="text-4xl font-bold text-primary">{blogPosts.length}</p>
                <p className="text-sm text-gray-400 mt-1">{blogPosts.filter(p => p.is_published).length} published</p>
                <p className="text-xs text-primary mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to manage →</p>
              </div>
              <div 
                onClick={() => setActiveTab('gallery')}
                className="glass rounded-2xl p-6 cursor-pointer hover:bg-white/10 transition-all group"
                data-testid="dashboard-gallery-card"
              >
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2 group-hover:text-primary transition-colors">Gallery Images</h3>
                <p className="text-4xl font-bold text-primary">{galleryItems.length}</p>
                <p className="text-sm text-gray-400 mt-1">images</p>
                <p className="text-xs text-primary mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to manage →</p>
              </div>
              <div 
                onClick={() => setActiveTab('contacts')}
                className="glass rounded-2xl p-6 cursor-pointer hover:bg-white/10 transition-all group"
                data-testid="dashboard-contacts-card"
              >
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2 group-hover:text-primary transition-colors">Contact Inquiries</h3>
                <p className="text-4xl font-bold text-primary">{contacts.length}</p>
                <p className="text-sm text-gray-400 mt-1">{contacts.filter(c => c.status === 'new').length} new</p>
                <p className="text-xs text-primary mt-3 opacity-0 group-hover:opacity-100 transition-opacity">Click to view →</p>
              </div>
              <div className="glass rounded-2xl p-6">
                <h3 className="text-xs text-gray-500 uppercase tracking-wider mb-2">Quick Actions</h3>
                <div className="flex flex-col gap-2 mt-2">
                  <Button size="sm" onClick={() => { setShowServiceForm(true); setActiveTab('services'); }} className="w-full bg-white/10 text-white hover:bg-white/20">
                    <Plus size={14} className="mr-1" /> Add Service
                  </Button>
                  <Button size="sm" onClick={() => { setShowBlogForm(true); setActiveTab('blog'); }} className="w-full bg-white/10 text-white hover:bg-white/20">
                    <Plus size={14} className="mr-1" /> Add Blog Post
                  </Button>
                  <Button size="sm" onClick={() => { setShowGalleryForm(true); setActiveTab('gallery'); }} className="w-full bg-white/10 text-white hover:bg-white/20">
                    <Plus size={14} className="mr-1" /> Add Gallery
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Store Tab */}
          {activeTab === 'store' && (
            <StoreManagement token={token} />
          )}

          {/* Hero Slides Tab */}
          {activeTab === 'hero' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center flex-wrap gap-3">
                <h2 className="text-xl font-heading font-semibold">Manage Hero Slides ({heroSlides.length})</h2>
                <div className="flex gap-2">
                  {heroSlides.length === 0 && (
                    <Button
                      onClick={async () => {
                        try {
                          const response = await axios.post(`${API_URL}/hero-slides/seed-defaults`, {}, {
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          toast.success(response.data.message);
                          fetchAllData();
                        } catch (error) {
                          toast.error('Failed to seed default slides');
                        }
                      }}
                      data-testid="seed-slides-button"
                      className="bg-indigo-600 text-white hover:bg-indigo-700"
                    >
                      <Plus size={16} className="mr-2" /> Seed Default Slides
                    </Button>
                  )}
                  <Button
                    onClick={() => { 
                      setSlideForm({
                        heading: '',
                        subheading: '',
                        category: 'Featured',
                        image_url: '',
                        cta_primary_text: 'Explore Services',
                        cta_primary_link: '/services',
                        cta_secondary_text: 'Get Proposal',
                        cta_secondary_link: '/contact',
                        display_order: heroSlides.length,
                        is_active: true
                      });
                      setEditingSlide(null);
                      setShowSlideForm(true); 
                    }}
                    data-testid="add-slide-button"
                    className="bg-primary text-black hover:bg-primary-hover"
                  >
                    <Plus size={16} className="mr-2" /> Add Slide
                  </Button>
                </div>
              </div>

              {/* Slide Form Modal */}
              <AnimatePresence>
                {showSlideForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="glass-heavy rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">{editingSlide ? 'Edit Slide' : 'Add New Slide'}</h3>
                      <button onClick={() => setShowSlideForm(false)} className="text-gray-400 hover:text-white">
                        <X size={20} />
                      </button>
                    </div>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      try {
                        const headers = { Authorization: `Bearer ${token}` };
                        if (editingSlide) {
                          await axios.put(`${API_URL}/hero-slides/${editingSlide.id}`, slideForm, { headers });
                          toast.success('Slide updated!');
                        } else {
                          await axios.post(`${API_URL}/hero-slides`, slideForm, { headers });
                          toast.success('Slide created!');
                        }
                        setShowSlideForm(false);
                        setEditingSlide(null);
                        fetchAllData();
                      } catch (error) {
                        toast.error('Failed to save slide');
                      }
                    }} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Heading *</label>
                          <Input
                            value={slideForm.heading}
                            onChange={(e) => setSlideForm({ ...slideForm, heading: e.target.value })}
                            required
                            placeholder="Main headline text"
                            data-testid="slide-heading-input"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Category Badge</label>
                          <Input
                            value={slideForm.category}
                            onChange={(e) => setSlideForm({ ...slideForm, category: e.target.value })}
                            placeholder="e.g., Featured, Drone Technology"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Subheading *</label>
                        <Textarea
                          value={slideForm.subheading}
                          onChange={(e) => setSlideForm({ ...slideForm, subheading: e.target.value })}
                          required
                          placeholder="Supporting description text"
                          data-testid="slide-subheading-input"
                          className="bg-white/5 border border-white/20 focus:border-primary"
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Background Image *</label>
                        <div className="flex gap-2">
                          <Input
                            value={slideForm.image_url}
                            onChange={(e) => setSlideForm({ ...slideForm, image_url: e.target.value })}
                            required
                            placeholder="URL or upload image"
                            data-testid="slide-image-input"
                            className="bg-white/5 border border-white/20 focus:border-primary flex-1"
                          />
                          <input
                            type="file"
                            ref={slideImageRef}
                            onChange={(e) => handleImageUpload(e.target.files[0], 'slide')}
                            accept="image/*"
                            className="hidden"
                          />
                          <Button
                            type="button"
                            onClick={() => slideImageRef.current?.click()}
                            disabled={uploading}
                            className="bg-white/10 hover:bg-white/20 px-3"
                          >
                            <Upload size={16} />
                          </Button>
                        </div>
                        {slideForm.image_url && (
                          <img src={slideForm.image_url} alt="Preview" className="mt-2 h-32 rounded object-cover w-full" />
                        )}
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Primary Button Text</label>
                          <Input
                            value={slideForm.cta_primary_text}
                            onChange={(e) => setSlideForm({ ...slideForm, cta_primary_text: e.target.value })}
                            placeholder="Explore Services"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Primary Button Link</label>
                          <Input
                            value={slideForm.cta_primary_link}
                            onChange={(e) => setSlideForm({ ...slideForm, cta_primary_link: e.target.value })}
                            placeholder="/services"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Secondary Button Text</label>
                          <Input
                            value={slideForm.cta_secondary_text}
                            onChange={(e) => setSlideForm({ ...slideForm, cta_secondary_text: e.target.value })}
                            placeholder="Get Proposal"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Secondary Button Link</label>
                          <Input
                            value={slideForm.cta_secondary_link}
                            onChange={(e) => setSlideForm({ ...slideForm, cta_secondary_link: e.target.value })}
                            placeholder="/contact"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={slideForm.is_active}
                            onChange={(e) => setSlideForm({ ...slideForm, is_active: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-600 bg-white/10 text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-gray-300">Active</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400">Display Order:</label>
                          <Input
                            type="number"
                            value={slideForm.display_order}
                            onChange={(e) => setSlideForm({ ...slideForm, display_order: parseInt(e.target.value) || 0 })}
                            className="w-20 bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" data-testid="save-slide-button" className="bg-primary text-black hover:bg-primary-hover">
                          <Save size={16} className="mr-2" /> {editingSlide ? 'Update' : 'Create'} Slide
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowSlideForm(false)}>Cancel</Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Slides List */}
              {heroSlides.length === 0 ? (
                <div className="glass rounded-xl p-8 text-center">
                  <Layers size={48} className="text-gray-500 mx-auto mb-4" />
                  <p className="text-gray-400 mb-4">No hero slides yet. Create your first slide to showcase on the homepage!</p>
                  <Button
                    onClick={() => setShowSlideForm(true)}
                    className="bg-primary text-black hover:bg-primary-hover"
                  >
                    <Plus size={16} className="mr-2" /> Create First Slide
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {heroSlides.sort((a, b) => a.display_order - b.display_order).map((slide) => (
                    <div 
                      key={slide.id} 
                      className={`glass rounded-xl overflow-hidden ${!slide.is_active ? 'opacity-60' : ''}`}
                      data-testid={`slide-item-${slide.id}`}
                    >
                      <div className="aspect-video relative">
                        <img 
                          src={slide.image_url} 
                          alt={slide.heading} 
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent" />
                        
                        {/* Status Badge */}
                        <div className="absolute top-3 left-3 flex gap-2">
                          <span className={`px-2 py-1 text-xs rounded-full ${slide.is_active ? 'bg-green-500/80 text-white' : 'bg-gray-500/80 text-white'}`}>
                            {slide.is_active ? 'Active' : 'Inactive'}
                          </span>
                          <span className="px-2 py-1 bg-primary/80 text-black text-xs rounded-full font-semibold">
                            Order: {slide.display_order}
                          </span>
                        </div>

                        {/* Action Buttons */}
                        <div className="absolute top-3 right-3 flex gap-1">
                          <button
                            onClick={async () => {
                              try {
                                await axios.put(`${API_URL}/hero-slides/${slide.id}/toggle-status`, {}, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                toast.success(`Slide ${slide.is_active ? 'deactivated' : 'activated'}!`);
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to toggle status');
                              }
                            }}
                            className={`p-2 rounded-lg ${slide.is_active ? 'bg-green-500/80 text-white hover:bg-green-500' : 'bg-gray-500/80 text-white hover:bg-gray-500'}`}
                            title={slide.is_active ? 'Deactivate' : 'Activate'}
                          >
                            {slide.is_active ? <Eye size={14} /> : <EyeOff size={14} />}
                          </button>
                          <button
                            onClick={() => {
                              setSlideForm({
                                heading: slide.heading,
                                subheading: slide.subheading,
                                category: slide.category,
                                image_url: slide.image_url,
                                cta_primary_text: slide.cta_primary_text,
                                cta_primary_link: slide.cta_primary_link,
                                cta_secondary_text: slide.cta_secondary_text,
                                cta_secondary_link: slide.cta_secondary_link,
                                display_order: slide.display_order,
                                is_active: slide.is_active
                              });
                              setEditingSlide(slide);
                              setShowSlideForm(true);
                            }}
                            className="p-2 rounded-lg bg-blue-500/80 text-white hover:bg-blue-500"
                            data-testid={`edit-slide-${slide.id}`}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Are you sure you want to delete this slide?')) return;
                              try {
                                await axios.delete(`${API_URL}/hero-slides/${slide.id}`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                toast.success('Slide deleted!');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to delete slide');
                              }
                            }}
                            className="p-2 rounded-lg bg-red-500/80 text-white hover:bg-red-500"
                            data-testid={`delete-slide-${slide.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>

                        {/* Content Preview */}
                        <div className="absolute bottom-3 left-3 right-3">
                          <span className="text-xs text-primary uppercase tracking-wider font-semibold">{slide.category}</span>
                          <h3 className="text-lg font-bold text-white line-clamp-2 mt-1">{slide.heading}</h3>
                          <p className="text-xs text-gray-300 line-clamp-1 mt-1">{slide.subheading}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* About Tab */}
          {activeTab === 'about' && about && (
            <div className="glass-heavy rounded-2xl p-6 space-y-4">
              <h2 className="text-xl font-heading font-semibold mb-4">Edit About Section</h2>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Title</label>
                <Input
                  value={about.title}
                  onChange={(e) => setAbout({ ...about, title: e.target.value })}
                  className="bg-white/5 border border-white/20 focus:border-primary"
                />
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Overview</label>
                <Textarea
                  value={about.overview}
                  onChange={(e) => setAbout({ ...about, overview: e.target.value })}
                  className="bg-white/5 border border-white/20 focus:border-primary"
                  rows={4}
                />
              </div>
              <div>
                <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Mission</label>
                <Textarea
                  value={about.mission}
                  onChange={(e) => setAbout({ ...about, mission: e.target.value })}
                  className="bg-white/5 border border-white/20 focus:border-primary"
                  rows={3}
                />
              </div>
              <Button onClick={handleAboutUpdate} className="bg-primary text-black hover:bg-primary-hover">
                <Save size={16} className="mr-2" /> Save Changes
              </Button>
            </div>
          )}

          {/* Services Tab */}
          {activeTab === 'services' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-heading font-semibold">Manage Services ({services.length})</h2>
                <Button
                  onClick={() => { resetServiceForm(); setShowServiceForm(true); }}
                  data-testid="add-service-button"
                  className="bg-primary text-black hover:bg-primary-hover"
                >
                  <Plus size={16} className="mr-2" /> Add Service
                </Button>
              </div>

              {/* Service Form Modal */}
              <AnimatePresence>
                {showServiceForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="glass-heavy rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">{editingService ? 'Edit Service' : 'Add New Service'}</h3>
                      <button onClick={resetServiceForm} className="text-gray-400 hover:text-white">
                        <X size={20} />
                      </button>
                    </div>
                    <form onSubmit={handleServiceSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Title *</label>
                          <Input
                            value={serviceForm.title}
                            onChange={(e) => setServiceForm({ ...serviceForm, title: e.target.value })}
                            required
                            data-testid="service-title-input"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Icon</label>
                          <select
                            value={serviceForm.icon}
                            onChange={(e) => setServiceForm({ ...serviceForm, icon: e.target.value })}
                            className="w-full bg-white/5 border border-white/20 rounded-md px-3 py-2 text-white focus:border-primary"
                          >
                            {iconOptions.map(icon => (
                              <option key={icon} value={icon} className="bg-gray-800">{icon}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Description *</label>
                        <Textarea
                          value={serviceForm.description}
                          onChange={(e) => setServiceForm({ ...serviceForm, description: e.target.value })}
                          required
                          data-testid="service-description-input"
                          className="bg-white/5 border border-white/20 focus:border-primary"
                          rows={3}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Features</label>
                        {serviceForm.features.map((feature, index) => (
                          <div key={index} className="flex gap-2 mb-2">
                            <Input
                              value={feature}
                              onChange={(e) => updateFeature(index, e.target.value)}
                              placeholder={`Feature ${index + 1}`}
                              className="bg-white/5 border border-white/20 focus:border-primary"
                            />
                            {serviceForm.features.length > 1 && (
                              <button type="button" onClick={() => removeFeature(index)} className="text-red-400 hover:text-red-300 px-2">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                        ))}
                        <Button type="button" variant="outline" size="sm" onClick={addFeature} className="mt-1">
                          <Plus size={14} className="mr-1" /> Add Feature
                        </Button>
                      </div>
                      <div className="flex items-center gap-4">
                        <label className="flex items-center gap-2 cursor-pointer">
                          <input
                            type="checkbox"
                            checked={serviceForm.is_active}
                            onChange={(e) => setServiceForm({ ...serviceForm, is_active: e.target.checked })}
                            className="w-4 h-4 rounded border-gray-600 bg-white/10 text-primary focus:ring-primary"
                          />
                          <span className="text-sm text-gray-300">Active</span>
                        </label>
                        <div className="flex items-center gap-2">
                          <label className="text-xs text-gray-400">Order:</label>
                          <Input
                            type="number"
                            value={serviceForm.order}
                            onChange={(e) => setServiceForm({ ...serviceForm, order: parseInt(e.target.value) || 0 })}
                            className="w-20 bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" data-testid="save-service-button" className="bg-primary text-black hover:bg-primary-hover">
                          <Save size={16} className="mr-2" /> {editingService ? 'Update' : 'Create'} Service
                        </Button>
                        <Button type="button" variant="outline" onClick={resetServiceForm}>Cancel</Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Services List */}
              <div className="space-y-3">
                {services.sort((a, b) => a.order - b.order).map((service) => (
                  <div key={service.id} className="glass rounded-xl p-4 flex items-center justify-between" data-testid={`service-item-${service.id}`}>
                    <div className="flex items-center gap-4">
                      <div className="text-gray-500">
                        <GripVertical size={20} />
                      </div>
                      <div>
                        <h3 className="font-semibold text-lg">{service.title}</h3>
                        <p className="text-sm text-gray-400 line-clamp-1">{service.description}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleToggleServiceActive(service)}
                        className={`p-2 rounded-lg ${service.is_active ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}
                        title={service.is_active ? 'Deactivate' : 'Activate'}
                      >
                        {service.is_active ? <Eye size={18} /> : <EyeOff size={18} />}
                      </button>
                      <button
                        onClick={() => handleEditService(service)}
                        className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30"
                        data-testid={`edit-service-${service.id}`}
                      >
                        <Edit size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteService(service.id)}
                        className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30"
                        data-testid={`delete-service-${service.id}`}
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Blog Tab */}
          {activeTab === 'blog' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-heading font-semibold">Manage Blog Posts ({blogPosts.length})</h2>
                <Button
                  onClick={() => { resetBlogForm(); setShowBlogForm(true); }}
                  data-testid="add-blog-button"
                  className="bg-primary text-black hover:bg-primary-hover"
                >
                  <Plus size={16} className="mr-2" /> Add Post
                </Button>
              </div>

              {/* Blog Form Modal */}
              <AnimatePresence>
                {showBlogForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="glass-heavy rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">{editingBlog ? 'Edit Blog Post' : 'Add New Blog Post'}</h3>
                      <button onClick={resetBlogForm} className="text-gray-400 hover:text-white">
                        <X size={20} />
                      </button>
                    </div>
                    <form onSubmit={handleBlogSubmit} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Title *</label>
                          <Input
                            value={blogForm.title}
                            onChange={(e) => setBlogForm({ ...blogForm, title: e.target.value })}
                            required
                            data-testid="blog-title-input"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Slug (URL)</label>
                          <Input
                            value={blogForm.slug}
                            onChange={(e) => setBlogForm({ ...blogForm, slug: e.target.value })}
                            placeholder="auto-generated-from-title"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Author</label>
                          <Input
                            value={blogForm.author}
                            onChange={(e) => setBlogForm({ ...blogForm, author: e.target.value })}
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Image</label>
                          <div className="flex gap-2">
                            <Input
                              value={blogForm.image_url}
                              onChange={(e) => setBlogForm({ ...blogForm, image_url: e.target.value })}
                              placeholder="URL or upload"
                              className="bg-white/5 border border-white/20 focus:border-primary flex-1"
                            />
                            <input
                              type="file"
                              ref={blogImageRef}
                              onChange={(e) => handleImageUpload(e.target.files[0], 'blog')}
                              accept="image/*"
                              className="hidden"
                            />
                            <Button
                              type="button"
                              onClick={() => blogImageRef.current?.click()}
                              disabled={uploading}
                              className="bg-white/10 hover:bg-white/20 px-3"
                            >
                              <Upload size={16} />
                            </Button>
                          </div>
                          {blogForm.image_url && (
                            <img src={blogForm.image_url} alt="Preview" className="mt-2 h-20 rounded object-cover" />
                          )}
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Excerpt *</label>
                        <Textarea
                          value={blogForm.excerpt}
                          onChange={(e) => setBlogForm({ ...blogForm, excerpt: e.target.value })}
                          required
                          placeholder="Brief summary of the post..."
                          data-testid="blog-excerpt-input"
                          className="bg-white/5 border border-white/20 focus:border-primary"
                          rows={2}
                        />
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Content *</label>
                        <div className="quill-dark-theme">
                          <ReactQuill
                            theme="snow"
                            value={blogForm.content}
                            onChange={(content) => setBlogForm({ ...blogForm, content })}
                            placeholder="Write your blog post content here..."
                            modules={quillModules}
                            formats={quillFormats}
                            style={{ minHeight: '300px' }}
                          />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Tags</label>
                        <div className="flex flex-wrap gap-2">
                          {blogForm.tags.map((tag, index) => (
                            <div key={index} className="flex items-center gap-1">
                              <Input
                                value={tag}
                                onChange={(e) => updateTag(index, e.target.value)}
                                placeholder="Tag"
                                className="w-32 bg-white/5 border border-white/20 focus:border-primary"
                              />
                              {blogForm.tags.length > 1 && (
                                <button type="button" onClick={() => removeTag(index)} className="text-red-400 hover:text-red-300">
                                  <X size={14} />
                                </button>
                              )}
                            </div>
                          ))}
                          <Button type="button" variant="outline" size="sm" onClick={addTag}>
                            <Plus size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <input
                          type="checkbox"
                          checked={blogForm.is_published}
                          onChange={(e) => setBlogForm({ ...blogForm, is_published: e.target.checked })}
                          className="w-4 h-4 rounded border-gray-600 bg-white/10 text-primary focus:ring-primary"
                        />
                        <span className="text-sm text-gray-300">Publish immediately</span>
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" data-testid="save-blog-button" className="bg-primary text-black hover:bg-primary-hover">
                          <Save size={16} className="mr-2" /> {editingBlog ? 'Update' : 'Create'} Post
                        </Button>
                        <Button type="button" variant="outline" onClick={resetBlogForm}>Cancel</Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Blog Posts List */}
              {blogPosts.length === 0 ? (
                <div className="glass rounded-xl p-8 text-center">
                  <p className="text-gray-400">No blog posts yet. Create your first post!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {blogPosts.map((post) => (
                    <div key={post.id} className="glass rounded-xl overflow-hidden" data-testid={`blog-item-${post.id}`}>
                      <div className="flex flex-col md:flex-row">
                        {/* Image */}
                        {post.image_url && (
                          <div className="md:w-48 h-40 md:h-auto flex-shrink-0">
                            <img src={post.image_url} alt={post.title} className="w-full h-full object-cover" />
                          </div>
                        )}
                        {/* Content */}
                        <div className="flex-1 p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <span className={`px-2 py-0.5 text-xs rounded-full ${post.is_published ? 'bg-green-500/20 text-green-400' : 'bg-yellow-500/20 text-yellow-400'}`}>
                                  {post.is_published ? 'Published' : 'Draft'}
                                </span>
                              </div>
                              <h3 className="font-semibold text-lg text-white mb-2 line-clamp-2">{post.title}</h3>
                              <p className="text-sm text-gray-400 mb-3 line-clamp-2">{post.excerpt}</p>
                              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                                <span className="flex items-center gap-1">
                                  <User size={12} /> {post.author}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Calendar size={12} /> {new Date(post.created_at).toLocaleDateString()}
                                </span>
                                {post.tags && post.tags.length > 0 && (
                                  <div className="flex gap-1">
                                    {post.tags.slice(0, 3).map((tag, idx) => (
                                      <span key={idx} className="px-2 py-0.5 bg-primary/10 text-primary rounded text-xs">{tag}</span>
                                    ))}
                                  </div>
                                )}
                              </div>
                            </div>
                            {/* Actions */}
                            <div className="flex sm:flex-col gap-2">
                              <button
                                onClick={() => handleToggleBlogPublish(post)}
                                className={`p-2 rounded-lg transition-colors ${post.is_published ? 'bg-green-500/20 text-green-400 hover:bg-green-500/30' : 'bg-yellow-500/20 text-yellow-400 hover:bg-yellow-500/30'}`}
                                title={post.is_published ? 'Unpublish' : 'Publish'}
                              >
                                {post.is_published ? <Eye size={18} /> : <EyeOff size={18} />}
                              </button>
                              <button
                                onClick={() => handleEditBlog(post)}
                                className="p-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
                                data-testid={`edit-blog-${post.id}`}
                              >
                                <Edit size={18} />
                              </button>
                              <button
                                onClick={() => handleDeleteBlog(post.id)}
                                className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 transition-colors"
                                data-testid={`delete-blog-${post.id}`}
                              >
                                <Trash2 size={18} />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Contacts Tab */}
          {activeTab === 'contacts' && (
            <div className="space-y-6">
              <h2 className="text-xl font-heading font-semibold">Contact Submissions ({contacts.length})</h2>
              {contacts.length === 0 ? (
                <div className="glass rounded-xl p-8 text-center">
                  <p className="text-gray-400">No contact submissions yet.</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {contacts.map((contact) => (
                    <div key={contact.id} data-testid={`contact-item-${contact.id}`} className="glass rounded-xl p-5">
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="font-semibold text-lg">{contact.name}</h3>
                          <p className="text-sm text-gray-400">{contact.institution}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-1 rounded text-xs ${contact.status === 'new' ? 'bg-green-500/20 text-green-400' : 'bg-gray-500/20 text-gray-400'}`}>
                            {contact.status}
                          </span>
                          <span className="text-xs text-gray-500">{new Date(contact.created_at).toLocaleDateString()}</span>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Are you sure you want to delete this contact?')) return;
                              try {
                                await axios.delete(`${API_URL}/contact/${contact.id}`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                toast.success('Contact deleted!');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to delete contact');
                              }
                            }}
                            className="p-2 rounded-lg bg-red-500/20 text-red-400 hover:bg-red-500/30 ml-2"
                            data-testid={`delete-contact-${contact.id}`}
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm mb-3">
                        <div>
                          <span className="text-xs text-gray-500 block">Email</span>
                          <a href={`mailto:${contact.email}`} className="text-primary hover:underline">{contact.email}</a>
                        </div>
                        <div>
                          <span className="text-xs text-gray-500 block">Phone</span>
                          <a href={`tel:${contact.phone}`} className="text-primary hover:underline">{contact.phone}</a>
                        </div>
                      </div>
                      <div className="mb-3">
                        <span className="text-xs text-gray-500 block mb-1">Services Required</span>
                        <div className="flex flex-wrap gap-1">
                          {contact.services_required.map((service, idx) => (
                            <span key={idx} className="px-2 py-1 bg-primary/20 text-primary rounded text-xs">{service}</span>
                          ))}
                        </div>
                      </div>
                      <div>
                        <span className="text-xs text-gray-500 block mb-1">Message</span>
                        <p className="text-gray-300 text-sm">{contact.message}</p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Gallery Tab */}
          {activeTab === 'gallery' && (
            <div className="space-y-6">
              <div className="flex justify-between items-center">
                <h2 className="text-xl font-heading font-semibold">Manage Gallery ({galleryItems.length})</h2>
                <Button
                  onClick={() => { 
                    setGalleryForm({ title: '', category: 'Labs', image_url: '', description: '', order: galleryItems.length });
                    setEditingGallery(null);
                    setShowGalleryForm(true); 
                  }}
                  data-testid="add-gallery-button"
                  className="bg-primary text-black hover:bg-primary-hover"
                >
                  <Plus size={16} className="mr-2" /> Add Image
                </Button>
              </div>

              {/* Gallery Form Modal */}
              <AnimatePresence>
                {showGalleryForm && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -20 }}
                    className="glass-heavy rounded-2xl p-6"
                  >
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="text-lg font-semibold">{editingGallery ? 'Edit Gallery Item' : 'Add New Gallery Item'}</h3>
                      <button onClick={() => setShowGalleryForm(false)} className="text-gray-400 hover:text-white">
                        <X size={20} />
                      </button>
                    </div>
                    <form onSubmit={async (e) => {
                      e.preventDefault();
                      try {
                        const headers = { Authorization: `Bearer ${token}` };
                        if (editingGallery) {
                          await axios.put(`${API_URL}/gallery/${editingGallery.id}`, galleryForm, { headers });
                          toast.success('Gallery item updated!');
                        } else {
                          await axios.post(`${API_URL}/gallery`, galleryForm, { headers });
                          toast.success('Gallery item created!');
                        }
                        setShowGalleryForm(false);
                        setEditingGallery(null);
                        fetchAllData();
                      } catch (error) {
                        toast.error('Failed to save gallery item');
                      }
                    }} className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Title *</label>
                          <Input
                            value={galleryForm.title}
                            onChange={(e) => setGalleryForm({ ...galleryForm, title: e.target.value })}
                            required
                            data-testid="gallery-title-input"
                            className="bg-white/5 border border-white/20 focus:border-primary"
                          />
                        </div>
                        <div>
                          <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Category</label>
                          <select
                            value={galleryForm.category}
                            onChange={(e) => setGalleryForm({ ...galleryForm, category: e.target.value })}
                            className="w-full bg-white/5 border border-white/20 rounded-md px-3 py-2 text-white focus:border-primary"
                          >
                            {galleryCategories.map(cat => (
                              <option key={cat} value={cat} className="bg-gray-800">{cat}</option>
                            ))}
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Image *</label>
                        <div className="flex gap-2">
                          <Input
                            value={galleryForm.image_url}
                            onChange={(e) => setGalleryForm({ ...galleryForm, image_url: e.target.value })}
                            required
                            placeholder="URL or upload image"
                            data-testid="gallery-url-input"
                            className="bg-white/5 border border-white/20 focus:border-primary flex-1"
                          />
                          <input
                            type="file"
                            ref={galleryImageRef}
                            onChange={(e) => handleImageUpload(e.target.files[0], 'gallery')}
                            accept="image/*"
                            className="hidden"
                          />
                          <Button
                            type="button"
                            onClick={() => galleryImageRef.current?.click()}
                            disabled={uploading}
                            className="bg-white/10 hover:bg-white/20 px-3"
                          >
                            <Upload size={16} />
                          </Button>
                        </div>
                        {galleryForm.image_url && (
                          <img src={galleryForm.image_url} alt="Preview" className="mt-2 h-24 rounded object-cover" />
                        )}
                      </div>
                      <div>
                        <label className="text-xs font-bold tracking-wider uppercase text-primary mb-1 block">Description</label>
                        <Textarea
                          value={galleryForm.description}
                          onChange={(e) => setGalleryForm({ ...galleryForm, description: e.target.value })}
                          placeholder="Optional description..."
                          className="bg-white/5 border border-white/20 focus:border-primary"
                          rows={2}
                        />
                      </div>
                      <div className="flex gap-2">
                        <Button type="submit" data-testid="save-gallery-button" className="bg-primary text-black hover:bg-primary-hover">
                          <Save size={16} className="mr-2" /> {editingGallery ? 'Update' : 'Create'} Item
                        </Button>
                        <Button type="button" variant="outline" onClick={() => setShowGalleryForm(false)}>Cancel</Button>
                      </div>
                    </form>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Gallery Items List */}
              {galleryItems.length === 0 ? (
                <div className="glass rounded-xl p-8 text-center">
                  <p className="text-gray-400">No gallery items yet. Add your first image!</p>
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {galleryItems.map((item) => (
                    <div key={item.id} className="glass rounded-xl overflow-hidden" data-testid={`gallery-item-${item.id}`}>
                      <div className="aspect-video relative">
                        <img src={item.image_url} alt={item.title} className="w-full h-full object-cover" />
                        <div className="absolute top-2 right-2 flex gap-1">
                          <button
                            onClick={() => {
                              setGalleryForm({
                                title: item.title,
                                category: item.category,
                                image_url: item.image_url,
                                description: item.description || '',
                                order: item.order
                              });
                              setEditingGallery(item);
                              setShowGalleryForm(true);
                            }}
                            className="p-2 rounded-lg bg-blue-500/80 text-white hover:bg-blue-500"
                            data-testid={`edit-gallery-${item.id}`}
                          >
                            <Edit size={14} />
                          </button>
                          <button
                            onClick={async () => {
                              if (!window.confirm('Are you sure you want to delete this image?')) return;
                              try {
                                await axios.delete(`${API_URL}/gallery/${item.id}`, {
                                  headers: { Authorization: `Bearer ${token}` },
                                });
                                toast.success('Gallery item deleted!');
                                fetchAllData();
                              } catch (error) {
                                toast.error('Failed to delete gallery item');
                              }
                            }}
                            className="p-2 rounded-lg bg-red-500/80 text-white hover:bg-red-500"
                            data-testid={`delete-gallery-${item.id}`}
                          >
                            <Trash2 size={14} />
                          </button>
                        </div>
                        <div className="absolute bottom-2 left-2">
                          <span className="px-2 py-1 bg-black/60 text-primary text-xs rounded">{item.category}</span>
                        </div>
                      </div>
                      <div className="p-3">
                        <h3 className="font-semibold truncate">{item.title}</h3>
                        {item.description && <p className="text-xs text-gray-400 truncate">{item.description}</p>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default Admin;
